import { CORE_API_URL } from '../config';

// The public sign-up funnel: create the agency, prove the address, choose a
// password. All three moved to the Go core API (cmd/api/signup.go) in slice A2.
//
// This file exists as much to REMOVE something as to add it. SignUpForm.jsx and
// VerifyEmail.jsx each carried their own copy of the API-base fallback --
// including a hardcoded production Cloud Run hostname -- so a developer running
// locally without REACT_APP_API_URL was posting real sign-ups at production.
// One import of config.js replaces both copies and puts the calls somewhere a
// test can reach them.
//
// None of the three sends an Authorization header, and none should: the caller
// has no account yet, and on the last two the token in the body IS the
// credential.

// detailOf pulls the API's error text out of a failed response. Every Go
// endpoint answers {"detail": "..."} -- the envelope cmd/api keeps precisely so
// the frontend has one error contract while endpoints migrate -- but a 502 from
// the load balancer is not JSON at all, so parsing has to be allowed to fail.
async function detailOf(response, fallback) {
  try {
    const body = await response.json();
    if (body && body.detail) return body.detail;
  } catch {
    // Not JSON. The status-based fallback is the best we can say.
  }
  return fallback;
}

// signUp creates a draft agency and its administrator, and triggers the
// verification email.
//
// The response is DELIBERATELY thin -- {status, message} and nothing else. The
// Python endpoint returned tenant_id and user_id; the Go one does not, because
// returning them only when something was created would let a caller tell "new
// address" from "already registered" by the shape of the reply, which is the
// email-enumeration leak A2 closed. Nothing here needs them: the caller shows a
// "check your email" panel.
export async function signUp(payload) {
  const response = await fetch(`${CORE_API_URL}/api/public/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await detailOf(response, 'Failed to submit sign-up form'));
  }
  return response.json();
}

// verifyEmail spends the link from the verification email. It is safe to call
// twice with the same token -- the endpoint is idempotent, which matters because
// VerifyEmail.jsx calls it from a mount effect and React 18 StrictMode invokes
// those twice in development.
export async function verifyEmail(token) {
  const response = await fetch(`${CORE_API_URL}/api/public/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw new Error(await detailOf(response, 'Email verification failed'));
  }
  return response.json();
}

// setPassword finishes the sign-up. The SAME token verifyEmail was given: the
// server burns it here, not there, so this call must come second and can only
// happen once.
export async function setPassword(token, password) {
  const response = await fetch(`${CORE_API_URL}/api/public/set-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    throw new Error(await detailOf(response, `Failed to set password (${response.status})`));
  }
  return response.json();
}

export const signupService = {
  signUp,
  verifyEmail,
  setPassword,
};
